import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../../theme';
import { FiHome, FiX, FiUsers, FiShield, FiBriefcase, FiMapPin, FiClock, FiDollarSign, FiPackage } from 'react-icons/fi';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';

const SidebarContainer = styled.aside`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: ${theme.colors.dark.background.primary};
  color: ${theme.colors.dark.text.primary};
`;

const SidebarHeader = styled.div`
  padding: ${theme.spacing[6]};
  border-bottom: 1px solid ${theme.colors.dark.border.light};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const LogoIcon = styled.div<{ hasImage?: boolean }>`
  width: 100%;
  max-width: 180px;
  height: auto;
  min-height: 50px;
  background: ${({ hasImage }) =>
    hasImage ? 'transparent' : `linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.primary[700]})`};
  border-radius: ${theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.white};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: auto;
    object-fit: contain;
    display: block;
  }
`;

const CloseButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${theme.borderRadius.md};
  border: none;
  background-color: transparent;
  color: ${theme.colors.dark.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.dark.background.secondary};
    color: ${theme.colors.white};
  }

  @media (max-width: ${theme.breakpoints.lg}) {
    display: flex;
  }
`;

const NavSection = styled.nav`
  flex: 1;
  padding: ${theme.spacing[4]} 0;
  overflow-y: auto;
`;

const NavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const NavItem = styled.li<{ active?: boolean }>`
  margin: ${theme.spacing[1]} 0;

  a {
    display: flex;
    align-items: center;
    gap: ${theme.spacing[3]};
    padding: ${theme.spacing[3]} ${theme.spacing[6]};
    color: ${({ active }) =>
    active ? theme.colors.white : theme.colors.dark.text.secondary};
    text-decoration: none;
    font-size: ${theme.typography.fontSize.sm};
    font-weight: ${({ active }) =>
    active ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.normal};
    background-color: ${({ active }) =>
    active ? `${theme.colors.primary[500]}20` : 'transparent'};
    border-left: 3px solid
      ${({ active }) => (active ? theme.colors.primary[500] : 'transparent')};
    transition: all 0.2s ease;
    cursor: pointer;

    &:hover {
      background-color: ${({ active }) =>
    active ? `${theme.colors.primary[500]}30` : theme.colors.dark.background.secondary};
      color: ${theme.colors.white};
    }

    svg {
      font-size: ${theme.typography.fontSize.lg};
      flex-shrink: 0;
    }
  }
`;

const DropdownNavItem = styled.li<{ isOpen?: boolean }>`
  margin: ${theme.spacing[1]} 0;
`;

const DropdownToggle = styled.div<{ active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  color: ${({ active }) =>
    active ? theme.colors.white : theme.colors.dark.text.secondary};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${({ active }) =>
    active ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.normal};
  background-color: ${({ active }) =>
    active ? `${theme.colors.primary[500]}20` : 'transparent'};
  border-left: 3px solid
    ${({ active }) => (active ? theme.colors.primary[500] : 'transparent')};
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background-color: ${({ active }) =>
    active ? `${theme.colors.primary[500]}30` : theme.colors.dark.background.secondary};
    color: ${theme.colors.white};
  }

  svg {
    font-size: ${theme.typography.fontSize.lg};
    flex-shrink: 0;
  }
`;

const DropdownMenu = styled.ul<{ isOpen: boolean }>`
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: ${({ isOpen }) => (isOpen ? '300px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const DropdownMenuItem = styled.li<{ active?: boolean }>`
  a {
    display: flex;
    align-items: center;
    gap: ${theme.spacing[3]};
    padding: ${theme.spacing[2]} ${theme.spacing[6]} ${theme.spacing[2]} ${theme.spacing[12]};
    color: ${({ active }) =>
    active ? theme.colors.white : theme.colors.dark.text.secondary};
    text-decoration: none;
    font-size: ${theme.typography.fontSize.sm};
    font-weight: ${({ active }) =>
    active ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.normal};
    background-color: ${({ active }) =>
    active ? `${theme.colors.primary[500]}20` : 'transparent'};
    transition: all 0.2s ease;
    cursor: pointer;

    &:hover {
      background-color: ${({ active }) =>
    active ? `${theme.colors.primary[500]}30` : theme.colors.dark.background.secondary};
      color: ${theme.colors.white};
    }
  }
`;

const SidebarFooter = styled.div`
  padding: ${theme.spacing[4]} ${theme.spacing[6]};
  border-top: 1px solid ${theme.colors.dark.border.light};
`;

const FooterText = styled.p`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.dark.text.muted};
  text-align: center;
  margin: 0;
`;

interface MenuItem {
  icon: React.ReactNode;
  label: string;
  path: string;
}

interface StaffSidebarProps {
  onClose?: () => void;
}

const StaffSidebar: React.FC<StaffSidebarProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { companyData, staffData, user } = useAuth();
  const [clientsDropdownOpen, setClientsDropdownOpen] = useState(false);
  const [deploymentDropdownOpen, setDeploymentDropdownOpen] = useState(false);
  const [financeDropdownOpen, setFinanceDropdownOpen] = useState(false);
  const [inventoryDropdownOpen, setInventoryDropdownOpen] = useState(false);

  // Check if clients dropdown should be open based on current route
  React.useEffect(() => {
    if (location.pathname.startsWith('/manage-clients')) {
      setClientsDropdownOpen(true);
    }
    if (location.pathname.startsWith('/deployment')) {
      setDeploymentDropdownOpen(true);
    }
    if (location.pathname.startsWith('/finance')) {
      setFinanceDropdownOpen(true);
    }
    if (location.pathname.startsWith('/inventory')) {
      setInventoryDropdownOpen(true);
    }
  }, [location.pathname]);

  // Check if user is a company owner/admin (logged in from companies, not staff)
  // Company owners have full access regardless of staff permissions
  const userEmail = user?.email?.toLowerCase();
  const isCompanyOwner = !!companyData && (
    !staffData || // No staffData = pure company owner
    (userEmail && (
      companyData.email?.toLowerCase() === userEmail ||
      companyData.emails?.some((email: string) => email.toLowerCase() === userEmail)
    )) // Email matches company = company owner
  );

  // Check permissions for each section
  // Company owners always have full access
  // For staff: check both access flag AND permission level (view/edit/add/delete)
  // If permission is null, don't show the menu item even if access is true
  // Default: hide everything unless explicitly granted
  const hasStaffAccess = isCompanyOwner || (
    staffData?.access?.staff === true && 
    staffData?.permissions?.staff !== null &&
    staffData?.permissions?.staff !== undefined
  );
  const hasGuardsAccess = isCompanyOwner || (
    staffData?.access?.guards === true && 
    staffData?.permissions?.guards !== null &&
    staffData?.permissions?.guards !== undefined
  );
  const hasClientsAccess = isCompanyOwner || (
    staffData?.access?.clients === true && 
    staffData?.permissions?.clients !== null &&
    staffData?.permissions?.clients !== undefined
  );
  const hasDeploymentAccess = isCompanyOwner || (
    staffData?.access?.deployment === true && 
    staffData?.permissions?.deployment !== null &&
    staffData?.permissions?.deployment !== undefined
  );
  const hasAttendanceAccess = isCompanyOwner || (
    staffData?.access?.attendance === true && 
    staffData?.permissions?.attendance !== null &&
    staffData?.permissions?.attendance !== undefined
  );
  const hasFinanceAccess = isCompanyOwner || (
    staffData?.access?.finance === true && 
    staffData?.permissions?.finance !== null &&
    staffData?.permissions?.finance !== undefined
  );
  const hasInventoryAccess = isCompanyOwner || (
    staffData?.access?.inventory === true && 
    staffData?.permissions?.inventory !== null &&
    staffData?.permissions?.inventory !== undefined
  );
  const hasTransactionAccess = isCompanyOwner || (
    staffData?.access?.transaction === true && 
    staffData?.permissions?.transaction !== null &&
    staffData?.permissions?.transaction !== undefined
  );
  const hasInvoiceAccess = isCompanyOwner || (
    staffData?.access?.invoice === true && 
    staffData?.permissions?.invoice !== null &&
    staffData?.permissions?.invoice !== undefined
  );
  const hasExpenseAccess = isCompanyOwner || (
    staffData?.access?.expense === true && 
    staffData?.permissions?.expense !== null &&
    staffData?.permissions?.expense !== undefined
  );
  const hasInvoiceManagementAccess = isCompanyOwner || (
    staffData?.access?.invoiceManagement === true && 
    staffData?.permissions?.invoiceManagement !== null &&
    staffData?.permissions?.invoiceManagement !== undefined
  );
  const hasAccountReceivableAccess = isCompanyOwner || (
    staffData?.access?.accountReceivable === true && 
    staffData?.permissions?.accountReceivable !== null &&
    staffData?.permissions?.accountReceivable !== undefined
  );
  const hasProfitLossAccess = isCompanyOwner || (
    staffData?.access?.profitLoss === true && 
    staffData?.permissions?.profitLoss !== null &&
    staffData?.permissions?.profitLoss !== undefined
  );
  const hasPettyCashAccess = isCompanyOwner || (
    staffData?.access?.pettyCash === true && 
    staffData?.permissions?.pettyCash !== null &&
    staffData?.permissions?.pettyCash !== undefined
  );
  const hasExpenseManagementAccess = isCompanyOwner || (
    staffData?.access?.expenseManagement === true && 
    staffData?.permissions?.expenseManagement !== null &&
    staffData?.permissions?.expenseManagement !== undefined
  );

  // Build menu items based on permissions
  const menuItems: MenuItem[] = [
    { icon: <FiHome />, label: 'Dashboard', path: '/dashboard' },
    ...(hasStaffAccess ? [{ icon: <FiUsers />, label: 'Staff Information', path: '/staff' }] : []),
    ...(hasGuardsAccess ? [{ icon: <FiShield />, label: 'Manage Guards', path: '/manage-guards' }] : []),
    ...(hasAttendanceAccess ? [{ icon: <FiClock />, label: 'Attendance', path: '/attendance' }] : []),
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
    if (onClose) {
      onClose();
    }
  };

  const isClientsActive = location.pathname.startsWith('/manage-clients');
  const isDeploymentActive = location.pathname.startsWith('/deployment');
  const isFinanceActive = location.pathname.startsWith('/finance');

  return (
    <SidebarContainer>
      <SidebarHeader>
        <LogoSection>
          {(() => {
            const logoSrc =
              companyData?.companyImg ||
              companyData?.logo ||
              companyData?.logoUrl ||
              companyData?.companyLogo;

            return (
              <LogoIcon hasImage={!!logoSrc}>
                {logoSrc ? (
                  <img
                    src={logoSrc}
                    alt={companyData?.companyName || companyData?.name || 'Company'}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      if (target.parentElement) {
                        target.parentElement.textContent = '🛡️';
                      }
                    }}
                  />
                ) : (
                  '🛡️'
                )}
              </LogoIcon>
            );
          })()}
        </LogoSection>
        {onClose && (
          <CloseButton onClick={onClose}>
            <FiX size={20} />
          </CloseButton>
        )}
      </SidebarHeader>

      <NavSection>
        <NavList>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <NavItem key={item.path} active={isActive}>
                <a onClick={() => handleNavigation(item.path)}>
                  {item.icon}
                  <span>{item.label}</span>
                </a>
              </NavItem>
            );
          })}
          {/* Only show Deployment dropdown if staff has deployment access */}
          {hasDeploymentAccess && (
            <DropdownNavItem>
              <DropdownToggle
                active={isDeploymentActive}
                onClick={() => setDeploymentDropdownOpen(!deploymentDropdownOpen)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[3] }}>
                  <FiMapPin />
                  <span>Deployment</span>
                </div>
                {deploymentDropdownOpen ? <FaChevronUp /> : <FaChevronDown />}
              </DropdownToggle>
              <DropdownMenu isOpen={deploymentDropdownOpen}>
                <DropdownMenuItem active={location.pathname === '/deployment/individual'}>
                  <a onClick={() => handleNavigation('/deployment/individual')}>
                    Individual Deployment
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem active={location.pathname === '/deployment/factory'}>
                  <a onClick={() => handleNavigation('/deployment/factory')}>
                    Factory Deployment
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem active={location.pathname === '/deployment/bank'}>
                  <a onClick={() => handleNavigation('/deployment/bank')}>
                    Bank Deployment
                  </a>
                </DropdownMenuItem>
              </DropdownMenu>
            </DropdownNavItem>
          )}
          {/* Only show Manage Clients dropdown if staff has clients access */}
          {hasClientsAccess && (
            <DropdownNavItem>
              <DropdownToggle
                active={isClientsActive}
                onClick={() => setClientsDropdownOpen(!clientsDropdownOpen)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[3] }}>
                  <FiBriefcase />
                  <span>Manage Clients</span>
                </div>
                {clientsDropdownOpen ? <FaChevronUp /> : <FaChevronDown />}
              </DropdownToggle>
              <DropdownMenu isOpen={clientsDropdownOpen}>
                <DropdownMenuItem active={location.pathname === '/manage-clients/factory'}>
                  <a onClick={() => handleNavigation('/manage-clients/factory')}>
                    Factory Clients
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem active={location.pathname === '/manage-clients/individual'}>
                  <a onClick={() => handleNavigation('/manage-clients/individual')}>
                    Individual Clients
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem active={location.pathname === '/manage-clients/bank'}>
                  <a onClick={() => handleNavigation('/manage-clients/bank')}>
                    Bank Clients
                  </a>
                </DropdownMenuItem>
              </DropdownMenu>
            </DropdownNavItem>
          )}
          {/* Finance Management - only show if staff has finance access */}
          {hasFinanceAccess && (
            <DropdownNavItem>
              <DropdownToggle
                active={isFinanceActive}
                onClick={() => setFinanceDropdownOpen(!financeDropdownOpen)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[3] }}>
                  <FiDollarSign />
                  <span>Finance Management</span>
                </div>
                {financeDropdownOpen ? <FaChevronUp /> : <FaChevronDown />}
              </DropdownToggle>
              <DropdownMenu isOpen={financeDropdownOpen}>
                {/* Transaction - only show if staff has transaction access */}
                {hasTransactionAccess && (
                  <DropdownMenuItem active={location.pathname === '/finance/transaction'}>
                    <a onClick={() => handleNavigation('/finance/transaction')}>
                      Transaction
                    </a>
                  </DropdownMenuItem>
                )}
                {/* Invoice Revenue - only show if staff has invoice access */}
                {hasInvoiceAccess && (
                  <DropdownMenuItem active={location.pathname === '/finance/invoice-revenue'}>
                    <a onClick={() => handleNavigation('/finance/invoice-revenue')}>
                      Invoice Revenue
                    </a>
                  </DropdownMenuItem>
                )}
                {/* Expense - only show if staff has expense access */}
                {hasExpenseAccess && (
                  <DropdownMenuItem active={location.pathname === '/finance/expense'}>
                    <a onClick={() => handleNavigation('/finance/expense')}>
                      Expense
                    </a>
                  </DropdownMenuItem>
                )}
                {/* Invoice Management - only show if staff has invoice management access */}
                {hasInvoiceManagementAccess && (
                  <DropdownMenuItem active={location.pathname === '/finance/invoice-management'}>
                    <a onClick={() => handleNavigation('/finance/invoice-management')}>
                      Invoice Management
                    </a>
                  </DropdownMenuItem>
                )}
                {/* Account Receivable - only show if staff has account receivable access */}
                {hasAccountReceivableAccess && (
                  <DropdownMenuItem active={location.pathname === '/finance/account-receivable'}>
                    <a onClick={() => handleNavigation('/finance/account-receivable')}>
                      Account Receivable
                    </a>
                  </DropdownMenuItem>
                )}
                {/* Profit & Loss - only show if staff has profit loss access */}
                {hasProfitLossAccess && (
                  <DropdownMenuItem active={location.pathname === '/finance/profit-loss'}>
                    <a onClick={() => handleNavigation('/finance/profit-loss')}>
                      Profit & Loss
                    </a>
                  </DropdownMenuItem>
                )}
                {/* Petty Cash Book - only show if staff has petty cash access */}
                {hasPettyCashAccess && (
                  <DropdownMenuItem active={location.pathname === '/finance/petty-cash'}>
                    <a onClick={() => handleNavigation('/finance/petty-cash')}>
                      Petty Cash Book
                    </a>
                  </DropdownMenuItem>
                )}
                {/* Expense Management - only show if staff has expense management access */}
                {hasExpenseManagementAccess && (
                  <DropdownMenuItem active={location.pathname === '/finance/expense-management'}>
                    <a onClick={() => handleNavigation('/finance/expense-management')}>
                      Expense Management
                    </a>
                  </DropdownMenuItem>
                )}
              </DropdownMenu>
            </DropdownNavItem>
          )}
        </NavList>
      </NavSection>

      <SidebarFooter>
        <FooterText>© 2024 {companyData?.companyName || companyData?.name || 'Guard Security'}</FooterText>
      </SidebarFooter>
    </SidebarContainer>
  );
};

export default StaffSidebar;

